import z from "zod";

import type { OnlineAutoCheckP0 } from "@/integrations/multiresume/ai-quality/validator-ownership";
import type { ResumeData } from "@/schema/resume/data";
import type { Locale } from "@/utils/locale";

import { proxyAwareServerFetch } from "@/integrations/http/proxy-fetch";
import { stripHtml } from "@/utils/string";

import { hasExactProviderResponseModel } from "./provider-response-model";
import {
  aiEditResultSchema,
  fieldSuggestionSchema,
  type AiEditResult,
  type AiEditTarget,
  type FieldSuggestion,
  type SectionEditOutput,
} from "./section-editing-contract";
import {
  buildPhase4CommonToolProperties,
  buildPhase4ReviewOutputSchema,
  phase4AiTaskContracts,
  phase4CommonToolRequiredFields,
  phase4RequiredConfirmationSchema,
} from "./task-contracts";

export {
  aiEditResultSchema,
  aiEditTargetSchema,
  fieldSuggestionSchema,
  sectionEditOutputSchema,
} from "./section-editing-contract";
export type { AiEditResult, AiEditTarget, FieldSuggestion, SectionEditOutput } from "./section-editing-contract";

export type SectionEditConfig = {
  apiKey: string;
  baseURL: string;
  model: string;
  maxTokens: number;
  timeoutMs: number;
  requireExactResponseModel?: boolean;
};

export type SectionEditProviderOutput = SectionEditOutput & {
  usage: {
    promptCacheHitTokens?: number;
    promptCacheMissTokens?: number;
  };
};

export type OptimizeSectionWithDeepSeekInput = {
  target: AiEditTarget;
  resumeData: ResumeData;
  originalTarget: AiEditResult;
  currentDraft: AiEditResult;
  customPrompt: string;
  targetContext: string;
  locale?: Locale;
  requestedOperation: "generate" | "regenerate";
  config: SectionEditConfig;
  fetchImpl?: typeof fetch;
};

export class SectionEditError extends Error {
  constructor(
    message: string,
    readonly code: "INVALID_INPUT" | "PROVIDER_ERROR" | "INVALID_PROVIDER_RESPONSE" | "REQUEST_TIMEOUT",
    readonly validatorCheck: Extract<OnlineAutoCheckP0, "schema" | "required_fields" | "no_change"> = "schema",
  ) {
    super(message);
    this.name = "SectionEditError";
  }
}

const sectionEditToolCallSchema = z.object({
  function: z.object({
    name: z.string(),
    arguments: z.union([z.string(), z.record(z.string(), z.unknown())]),
  }),
});

const sectionEditResponseSchema = z.object({
  choices: z.array(
    z.object({
      message: z.object({
        tool_calls: z.array(sectionEditToolCallSchema).optional(),
      }),
    }),
  ),
  usage: z
    .object({
      prompt_cache_hit_tokens: z.number().optional(),
      prompt_cache_miss_tokens: z.number().optional(),
    })
    .passthrough()
    .optional(),
});

const sectionEditArgumentsSchema = z
  .object({
    schema_version: z.string(),
    prompt_version: z.string(),
    task_type: z.literal("section_item_optimize"),
    summary: z.string().default(""),
    source_evidence: z.array(z.string()).default([]),
    protected_fact_warnings: z.array(z.string()).default([]),
    gaps: z.array(z.string()).default([]),
    warnings: z.array(z.string()).default([]),
    confidence: z.enum(["high", "medium", "low"]),
    requires_user_confirmation: phase4RequiredConfirmationSchema,
    result: aiEditResultSchema,
    suggestions: z.array(z.string()).default([]),
    reasons: z.array(z.string()).default([]),
    fieldSuggestions: z.array(fieldSuggestionSchema).default([]),
  })
  .strict();

const emptyAiEditResult: AiEditResult = {
  contentHtml: "",
  descriptionHtml: "",
  position: "",
  name: "",
  roles: [],
};

function buildChatCompletionsUrl(baseURL: string) {
  return `${baseURL.replace(/\/+$/, "")}/chat/completions`;
}

function compactHtml(value: string) {
  return stripHtml(value).replace(/\s+/g, " ").trim();
}

function compactItems<T extends Record<string, unknown>>(
  items: T[],
  fields: Array<keyof T>,
): Array<Record<string, string>> {
  return items
    .map((item) => {
      const output: Record<string, string> = {};

      for (const field of fields) {
        const value = item[field];
        if (typeof value !== "string") continue;

        const text = compactHtml(value);
        if (text.length > 0) output[String(field)] = text;
      }

      return output;
    })
    .filter((item) => Object.keys(item).length > 0);
}

export function buildSectionEditResumeContext(data: ResumeData) {
  return {
    basics: {
      name: data.basics.name,
      headline: data.basics.headline,
      location: data.basics.location,
    },
    summary: compactHtml(data.summary.content),
    experience: data.sections.experience.items
      .map((item) => ({
        ...compactItems([item], ["company", "position", "location", "period", "description"])[0],
        website: compactHtml(item.website.label || item.website.url),
        roles: item.roles
          .map((role) => ({
            id: role.id,
            position: compactHtml(role.position),
            period: compactHtml(role.period),
            description: compactHtml(role.description),
          }))
          .filter((role) => role.position.length > 0 || role.period.length > 0 || role.description.length > 0),
      }))
      .filter((item) => Object.keys(item).some((key) => key !== "roles") || item.roles.length > 0),
    education: compactItems(data.sections.education.items, [
      "school",
      "degree",
      "area",
      "grade",
      "location",
      "period",
      "description",
    ]),
    projects: compactItems(data.sections.projects.items, ["name", "period", "description"]),
    skills: data.sections.skills.items
      .map((item) => ({
        name: item.name,
        keywords: item.keywords.filter((keyword) => keyword.trim().length > 0),
      }))
      .filter((item) => item.name.trim().length > 0 || item.keywords.length > 0),
    certifications: compactItems(data.sections.certifications.items, ["title", "issuer", "date", "description"]),
    languages: compactItems(data.sections.languages.items, ["language", "fluency"]),
  };
}

export function getSectionEditTargetSnapshot(data: ResumeData, target: AiEditTarget) {
  if (target.type === "summary") {
    return {
      label: "Summary",
      original: { ...emptyAiEditResult, contentHtml: data.summary.content },
      protectedFacts: [],
      allowedFieldSuggestionFields: [],
    };
  }

  if (target.type === "experience") {
    const item = data.sections.experience.items.find((entry) => entry.id === target.itemId);
    if (!item) throw new SectionEditError("Experience item was not found.", "INVALID_INPUT");

    return {
      label: "Experience item",
      original: {
        ...emptyAiEditResult,
        position: item.position,
        descriptionHtml: item.roles.length > 0 ? "" : item.description,
        roles: item.roles.map((role) => ({
          id: role.id,
          position: role.position,
          descriptionHtml: role.description,
        })),
      },
      protectedFacts: ["company", "location", "period", "website", "roles.period", "role ids"],
      allowedFieldSuggestionFields: ["position"],
    };
  }

  const item = data.sections.projects.items.find((entry) => entry.id === target.itemId);
  if (!item) throw new SectionEditError("Project item was not found.", "INVALID_INPUT");

  return {
    label: "Project item",
    original: { ...emptyAiEditResult, name: item.name, descriptionHtml: item.description },
    protectedFacts: ["period", "website", "unprovided technologies", "unprovided metrics"],
    allowedFieldSuggestionFields: ["name"],
  };
}

export function coerceAiEditResult(target: AiEditTarget, value: unknown, fallback: AiEditResult): AiEditResult {
  if (typeof value === "string") {
    if (target.type === "summary") return { ...emptyAiEditResult, contentHtml: value };
    return { ...fallback, descriptionHtml: value };
  }

  const parsed = aiEditResultSchema.partial().safeParse(value);
  if (!parsed.success) return fallback;

  return { ...fallback, ...parsed.data };
}

export function createEffectiveProposalRegenerationDraft(
  target: AiEditTarget,
  original: AiEditResult,
  proposal: AiEditResult,
): AiEditResult {
  if (target.type === "summary") {
    return compactHtml(proposal.contentHtml).length > 0
      ? { ...original, contentHtml: proposal.contentHtml }
      : structuredClone(original);
  }

  if (target.type === "project") {
    return {
      ...original,
      descriptionHtml:
        compactHtml(proposal.descriptionHtml).length > 0 ? proposal.descriptionHtml : original.descriptionHtml,
      name: proposal.name.trim().length > 0 ? proposal.name : original.name,
    };
  }

  const effective: AiEditResult = {
    ...original,
    position: proposal.position.trim().length > 0 ? proposal.position : original.position,
    descriptionHtml:
      original.roles.length === 0 && compactHtml(proposal.descriptionHtml).length > 0
        ? proposal.descriptionHtml
        : original.descriptionHtml,
  };
  if (original.roles.length === 0 || proposal.roles.length === 0) return effective;

  const proposalRoles = new Map(proposal.roles.map((role) => [role.id, role]));
  return {
    ...effective,
    roles: original.roles.map((role) => {
      const proposed = proposalRoles.get(role.id);
      if (!proposed) return role;
      return {
        ...role,
        position: proposed.position.trim().length > 0 ? proposed.position : role.position,
        descriptionHtml:
          compactHtml(proposed.descriptionHtml).length > 0 ? proposed.descriptionHtml : role.descriptionHtml,
      };
    }),
  };
}

function getTargetSuggestedText(target: AiEditTarget, result: AiEditResult) {
  if (target.type === "summary") return result.contentHtml;
  if (target.type === "experience" && result.roles.length > 0) {
    return result.roles.map((role) => role.descriptionHtml).join("\n");
  }
  return result.descriptionHtml;
}

function hasResultContent(target: AiEditTarget, result: AiEditResult) {
  const mainText = getTargetSuggestedText(target, result);
  if (compactHtml(mainText).length > 0) return true;

  if (target.type === "experience" && result.roles.some((role) => compactHtml(role.descriptionHtml).length > 0)) {
    return true;
  }
  if (target.type === "experience") return result.position.trim().length > 0;
  if (target.type === "project") return result.name.trim().length > 0;

  return false;
}

function compactPlain(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function hasDifferentPlainText(left: string, right: string) {
  return compactPlain(left) !== compactPlain(right);
}

function hasDifferentHtmlText(left: string, right: string) {
  return compactHtml(left) !== compactHtml(right);
}

export function hasMeaningfulSectionEditResult(target: AiEditTarget, fallback: AiEditResult, result: AiEditResult) {
  if (target.type === "summary") {
    return hasDifferentHtmlText(fallback.contentHtml, result.contentHtml);
  }

  if (target.type === "experience") {
    if (hasDifferentPlainText(fallback.position, result.position)) return true;

    if (fallback.roles.length > 0) {
      const fallbackRolesById = new Map(fallback.roles.map((role) => [role.id, role]));

      return result.roles.some((role) => {
        const fallbackRole = fallbackRolesById.get(role.id);
        if (!fallbackRole) return false;
        if (hasDifferentPlainText(fallbackRole.position, role.position)) return true;
        return hasDifferentHtmlText(fallbackRole.descriptionHtml, role.descriptionHtml);
      });
    }

    return hasDifferentHtmlText(fallback.descriptionHtml, result.descriptionHtml);
  }

  return (
    hasDifferentPlainText(fallback.name, result.name) ||
    hasDifferentHtmlText(fallback.descriptionHtml, result.descriptionHtml)
  );
}

function mergeRoleResults(fallback: AiEditResult, result: AiEditResult) {
  const fallbackRoles = fallback.roles ?? [];
  const resultRoles = result.roles ?? [];
  if (fallbackRoles.length === 0) return [];

  const rolesById = new Map(resultRoles.map((role) => [role.id, role]));

  return fallbackRoles.map((role) => {
    const generatedRole = rolesById.get(role.id);
    if (!generatedRole) return role;

    return {
      id: role.id,
      position: generatedRole.position.trim().length > 0 ? generatedRole.position : role.position,
      descriptionHtml:
        compactHtml(generatedRole.descriptionHtml).length > 0 ? generatedRole.descriptionHtml : role.descriptionHtml,
    };
  });
}

function normalizeSectionEditOutput(input: {
  target: AiEditTarget;
  fallback: AiEditResult;
  result: AiEditResult;
  suggestions: string[];
  reasons: string[];
  fieldSuggestions: FieldSuggestion[];
}): SectionEditOutput {
  const snapshot = {
    result: { ...input.result },
    suggestions: input.suggestions.map((value) => value.trim()).filter(Boolean),
    reasons: input.reasons.map((value) => value.trim()).filter(Boolean),
    fieldSuggestions: input.fieldSuggestions.filter((suggestion) => suggestion.suggestedValue.trim().length > 0),
  };

  if (input.target.type === "summary") {
    snapshot.result.descriptionHtml = "";
    snapshot.result.position = "";
    snapshot.result.name = "";
    snapshot.result.roles = [];
  }

  if (input.target.type === "experience") {
    snapshot.result.contentHtml = "";
    snapshot.result.name = "";
    if (snapshot.result.position.trim().length === 0) snapshot.result.position = input.fallback.position;
    snapshot.result.roles = mergeRoleResults(input.fallback, snapshot.result);
    if (snapshot.result.roles.length > 0) snapshot.result.descriptionHtml = "";
  }

  if (input.target.type === "project") {
    snapshot.result.contentHtml = "";
    snapshot.result.position = "";
    snapshot.result.roles = [];
    if (snapshot.result.name.trim().length === 0) snapshot.result.name = input.fallback.name;
  }

  if (!hasResultContent(input.target, snapshot.result)) {
    throw new SectionEditError(
      "DeepSeek returned an empty AI edit result.",
      "INVALID_PROVIDER_RESPONSE",
      "required_fields",
    );
  }

  const allowedFields = new Set(getSectionEditAllowedFieldSuggestions(input.target));
  snapshot.fieldSuggestions = snapshot.fieldSuggestions.filter((suggestion) => allowedFields.has(suggestion.field));

  return {
    ...snapshot,
    suggestedText: getTargetSuggestedText(input.target, snapshot.result),
  };
}

function buildCurrentFullTargetContext(data: ResumeData, target: AiEditTarget) {
  if (target.type === "summary") {
    return {
      content: compactHtml(data.summary.content),
    };
  }

  if (target.type === "experience") {
    const item = data.sections.experience.items.find((entry) => entry.id === target.itemId);
    if (!item) throw new SectionEditError("Experience item was not found.", "INVALID_INPUT");

    return {
      company: item.company,
      location: item.location,
      position: item.position,
      period: item.period,
      website: item.website.label || item.website.url,
      showLinkInTitle: item.options?.showLinkInTitle ?? false,
      description: compactHtml(item.description),
      roles: item.roles.map((role) => ({
        id: role.id,
        position: role.position,
        period: role.period,
        description: compactHtml(role.description),
      })),
    };
  }

  const item = data.sections.projects.items.find((entry) => entry.id === target.itemId);
  if (!item) throw new SectionEditError("Project item was not found.", "INVALID_INPUT");

  return {
    name: item.name,
    period: item.period,
    website: item.website.label || item.website.url,
    showLinkInTitle: item.options?.showLinkInTitle ?? false,
    description: compactHtml(item.description),
  };
}

function getSectionEditAllowedFieldSuggestions(target: AiEditTarget) {
  if (target.type === "experience") return ["position"];
  if (target.type === "project") return ["name"];
  return [];
}

function buildSectionEditInstruction(input: OptimizeSectionWithDeepSeekInput) {
  const hasCustomPrompt = input.customPrompt.trim().length > 0;
  const operationInstruction =
    input.requestedOperation === "generate"
      ? "Generate a fresh AI optimized version from the original target. Do not copy the original target or current AI result draft unchanged."
      : "Regenerate by refining the current AI result draft in place. Preserve reasonable user edits already present in the current draft. If the custom prompt is empty or unchanged, make only modest improvements and avoid large structural jumps. Do not repeat the draft unchanged.";

  const defaultInstruction = hasCustomPrompt
    ? "Follow the user custom prompt while still preserving protected facts and returning resume-ready content."
    : "No custom instruction was provided. Improve clarity, specificity, impact, concision, and professional tone while preserving protected facts.";

  return [
    operationInstruction,
    defaultInstruction,
    "If the target has no existing content, write a concise resume-ready draft from the stable resume context.",
    "If protected-fact rules prevent a requested change, keep the protected fact unchanged and explain the limitation in suggestions.",
  ].join(" ");
}

function getSectionEditOutputLanguage(locale: Locale | undefined) {
  if (locale === "zh-TW") return "Traditional Chinese";
  if (locale === "zh-CN") return "Simplified Chinese";
  return "English";
}

function buildSectionEditMessages(input: OptimizeSectionWithDeepSeekInput, retryInstruction = "") {
  const resumeContext = buildSectionEditResumeContext(input.resumeData);
  const targetSnapshot = getSectionEditTargetSnapshot(input.resumeData, input.target);
  const outputLanguage = getSectionEditOutputLanguage(input.locale);
  const contract = phase4AiTaskContracts.itemEdit;

  return [
    {
      role: "system",
      content: [
        `MultiResume AI section/item editing prompt ${contract.promptVersion}.`,
        `The output schema version is ${contract.schemaVersion}.`,
        "You optimize one Reactive Resume section object or section item object at a time.",
        "Return only by calling the submit_section_edit tool exactly once.",
        "The tool arguments must include schema_version, prompt_version, task_type, summary, source_evidence, protected_fact_warnings, gaps, warnings, confidence, and requires_user_confirmation.",
        "Use simple sanitized HTML for contentHtml and descriptionHtml, such as <p>...</p> and <ul><li>...</li></ul>.",
        "The result object uses contentHtml for Summary, position and descriptionHtml for single-role Experience, position and roles for Experience with role progression, and name and descriptionHtml for Project.",
        "For Experience role progression, return one roles item for each existing role id, preserving ids exactly. Do not invent, remove, or reorder role ids.",
        "Unused scalar result fields must be empty strings and unused roles must be an empty array.",
        "AI suggestions and reasons must be concise user-facing strings.",
        "Field suggestions are deprecated in Phase 2. Return fieldSuggestions as an empty array.",
        "Do not invent companies, dates, schools, credentials, locations, technologies, metrics, websites, ownership, or contact details.",
        "Protected facts must not be modified in the main result or field suggestions.",
        "If the user asks for a protected fact change, explain the risk in suggestions and keep the fact unchanged unless it is an allowed field suggestion.",
        "The main result must be a useful edited draft, not an identical copy, unless there is truly no safe edit to make.",
        `Write every user-facing suggestion, reason, and edited resume text in ${outputLanguage}. Preserve proper nouns, product names, company names, and technical terms as written when appropriate.`,
      ].join(" "),
    },
    {
      role: "user",
      content: [
        "Stable full resume context:",
        JSON.stringify(resumeContext),
        "",
        "Stable target context, if available:",
        input.targetContext.trim(),
        "",
        "Current edit target:",
        JSON.stringify({
          target: input.target,
          label: targetSnapshot.label,
          originalTarget: input.originalTarget,
          currentFullTarget: buildCurrentFullTargetContext(input.resumeData, input.target),
          protectedFacts: targetSnapshot.protectedFacts,
          allowedFieldSuggestionFields: targetSnapshot.allowedFieldSuggestionFields,
        }),
        "",
        "Current AI result draft:",
        JSON.stringify(input.currentDraft),
        "",
        "User custom prompt:",
        input.customPrompt.trim(),
        "",
        "Requested operation:",
        input.requestedOperation,
        "",
        "Editing instruction:",
        buildSectionEditInstruction(input),
        "",
        "Retry instruction:",
        retryInstruction,
      ].join("\n"),
    },
  ];
}

function buildSubmitSectionEditTool() {
  const contract = phase4AiTaskContracts.itemEdit;

  return {
    type: "function",
    function: {
      name: "submit_section_edit",
      description: "Submit a Phase 4 suggestion-first AI edit result for one resume section or item target.",
      parameters: {
        type: "object",
        properties: {
          ...buildPhase4CommonToolProperties(contract),
          result: {
            type: "object",
            properties: {
              contentHtml: {
                type: "string",
                description: "Summary rich-text HTML. Empty for non-Summary targets.",
              },
              descriptionHtml: {
                type: "string",
                description: "Experience or Project rich-text description HTML. Empty for Summary targets.",
              },
              position: {
                type: "string",
                description: "Experience position/title. Empty for non-Experience targets.",
              },
              name: {
                type: "string",
                description: "Project name. Empty for non-Project targets.",
              },
              roles: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    id: {
                      type: "string",
                      description: "Existing Experience role id. Preserve the input role id exactly.",
                    },
                    position: {
                      type: "string",
                      description: "Optimized role progression position/title for this existing role.",
                    },
                    descriptionHtml: {
                      type: "string",
                      description: "Optimized role progression rich-text description HTML for this existing role.",
                    },
                  },
                  required: ["id", "position", "descriptionHtml"],
                  additionalProperties: false,
                },
                description:
                  "Experience role progression edits. Empty for Summary, Project, and single-role Experience.",
              },
            },
            required: ["contentHtml", "descriptionHtml", "position", "name", "roles"],
            additionalProperties: false,
          },
          suggestions: {
            type: "array",
            items: { type: "string" },
            description: "Concise suggestions explaining what to improve.",
          },
          reasons: {
            type: "array",
            items: { type: "string" },
            description: "Concise reasons explaining why this edit is appropriate.",
          },
          fieldSuggestions: {
            type: "array",
            items: {
              type: "object",
              properties: {
                field: {
                  type: "string",
                  description: "Allowed values: position for Experience, name for Project. Empty array for Summary.",
                },
                label: { type: "string" },
                originalValue: { type: "string" },
                suggestedValue: { type: "string" },
                reason: { type: "string" },
              },
              required: ["field", "label", "originalValue", "suggestedValue", "reason"],
              additionalProperties: false,
            },
          },
        },
        required: [...phase4CommonToolRequiredFields, "result", "suggestions", "reasons", "fieldSuggestions"],
        additionalProperties: false,
      },
      strict: true,
    },
  };
}

function getFetchFailureMessage(error: TypeError) {
  const cause = error.cause;

  if (cause && typeof cause === "object" && "code" in cause) {
    return `DeepSeek section editing failed due to a network connection error (${String(cause.code)}).`;
  }

  return "DeepSeek section editing failed due to a network connection error.";
}

export function parseSectionEditResponse(
  responseBody: unknown,
  input: { target: AiEditTarget; fallback: AiEditResult },
): SectionEditProviderOutput {
  const parsedResponse = sectionEditResponseSchema.parse(responseBody);
  const message = parsedResponse.choices[0]?.message;

  if (!message) {
    throw new SectionEditError("DeepSeek returned no choices.", "INVALID_PROVIDER_RESPONSE", "required_fields");
  }

  const toolCall = message.tool_calls?.find((call) => call.function.name === "submit_section_edit");

  if (!toolCall) {
    throw new SectionEditError(
      "DeepSeek did not return the required submit_section_edit tool call.",
      "INVALID_PROVIDER_RESPONSE",
      "required_fields",
    );
  }

  const argumentsText =
    typeof toolCall.function.arguments === "string"
      ? toolCall.function.arguments
      : JSON.stringify(toolCall.function.arguments);

  const parsedArguments = sectionEditArgumentsSchema.parse(JSON.parse(argumentsText));
  buildPhase4ReviewOutputSchema(phase4AiTaskContracts.itemEdit).parse({
    schema_version: parsedArguments.schema_version,
    prompt_version: parsedArguments.prompt_version,
    task_type: parsedArguments.task_type,
    summary: parsedArguments.summary,
    source_evidence: parsedArguments.source_evidence,
    protected_fact_warnings: parsedArguments.protected_fact_warnings,
    gaps: parsedArguments.gaps,
    warnings: parsedArguments.warnings,
    confidence: parsedArguments.confidence,
    requires_user_confirmation: parsedArguments.requires_user_confirmation,
  });
  const output = normalizeSectionEditOutput({
    target: input.target,
    fallback: input.fallback,
    ...parsedArguments,
  });

  return {
    ...output,
    usage: {
      promptCacheHitTokens: parsedResponse.usage?.prompt_cache_hit_tokens,
      promptCacheMissTokens: parsedResponse.usage?.prompt_cache_miss_tokens,
    },
  };
}

function sanitizeProviderErrorText(text: string) {
  // Replace raw provider error text with a safe user-facing message.
  // Provider-level safety refusals, content policy rejections, and
  // internal error details should not be exposed to users.
  const lower = text.toLowerCase();
  if (
    lower.includes("safety") ||
    lower.includes("content_filter") ||
    lower.includes("refusal") ||
    lower.includes("cannot safely") ||
    lower.includes("blocked") ||
    lower.includes("content policy")
  ) {
    return "The AI service could not process this request. Try adjusting the content or your optimization direction.";
  }
  // For other provider errors, strip sensitive details but keep the generic message
  if (text.length > 200) {
    return "The AI service returned an unexpected error. Please try again.";
  }
  return text;
}

export async function optimizeSectionWithDeepSeek(input: OptimizeSectionWithDeepSeekInput) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), input.config.timeoutMs);
  const url = buildChatCompletionsUrl(input.config.baseURL);
  const fetchImpl = input.fetchImpl ?? proxyAwareServerFetch;

  console.info("[MultiResume AI] DeepSeek section editing request", {
    baseURL: input.config.baseURL,
    model: input.config.model,
    targetType: input.target.type,
    targetContextChars: input.targetContext.length,
    customPromptChars: input.customPrompt.length,
  });

  try {
    const requestEdit = async (retryInstruction = "") => {
      const response = await fetchImpl(url, {
        method: "POST",
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${input.config.apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: input.config.model,
          thinking: { type: "disabled" },
          messages: buildSectionEditMessages(input, retryInstruction),
          tools: [buildSubmitSectionEditTool()],
          tool_choice: { type: "function", function: { name: "submit_section_edit" } },
          temperature: 0.2,
          max_tokens: input.config.maxTokens,
        }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        const sanitized = sanitizeProviderErrorText(responseText);
        throw new SectionEditError(`AI service returned an error (${response.status}). ${sanitized}`, "PROVIDER_ERROR");
      }

      const responseBody = JSON.parse(responseText);
      if (input.config.requireExactResponseModel && !hasExactProviderResponseModel(responseBody, input.config.model)) {
        throw new SectionEditError(
          "AI response model did not match the configured model.",
          "INVALID_PROVIDER_RESPONSE",
        );
      }
      return parseSectionEditResponse(responseBody, {
        target: input.target,
        fallback: input.currentDraft,
      });
    };

    const firstOutput = await requestEdit();

    if (hasMeaningfulSectionEditResult(input.target, input.currentDraft, firstOutput.result)) {
      return firstOutput;
    }

    console.info("[MultiResume AI] DeepSeek section editing returned unchanged result; retrying", {
      targetType: input.target.type,
      customPromptChars: input.customPrompt.length,
    });

    const retryOutput = await requestEdit(
      "The previous result copied the current draft unchanged while still giving suggestions. This retry must put the suggested improvements into the result fields themselves. Return a visibly edited result in at least one AI-eligible field, while preserving protected facts.",
    );

    if (!hasMeaningfulSectionEditResult(input.target, input.currentDraft, retryOutput.result)) {
      throw new SectionEditError(
        "DeepSeek returned an unchanged AI edit result.",
        "INVALID_PROVIDER_RESPONSE",
        "no_change",
      );
    }

    return retryOutput;
  } catch (error) {
    if (error instanceof SectionEditError) throw error;

    if (error instanceof DOMException && error.name === "AbortError") {
      throw new SectionEditError("DeepSeek section editing timed out.", "REQUEST_TIMEOUT");
    }

    if (error instanceof TypeError && error.message === "fetch failed") {
      throw new SectionEditError(getFetchFailureMessage(error), "PROVIDER_ERROR");
    }

    if (error instanceof z.ZodError) {
      throw new SectionEditError(
        "DeepSeek section editing response shape was invalid.",
        "INVALID_PROVIDER_RESPONSE",
        "schema",
      );
    }

    if (error instanceof SyntaxError) {
      throw new SectionEditError(
        "DeepSeek section editing response was not valid JSON.",
        "INVALID_PROVIDER_RESPONSE",
        "schema",
      );
    }

    throw error;
  } finally {
    clearTimeout(timeout);
  }
}
